"use client";

import { useEffect } from "react";
import { create } from "zustand";
import { toast } from "@/shared/components/toast";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { addFavorite, getFavorites, removeFavorite } from "../services/favorite.service";
import type { FavoritesList } from "../types/favorite.dto";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { getActiveStoreId } from "@/modules/store/stores/useActiveStore";

/**
 * Favoritos do cliente logado — os IDS, em um lugar só.
 *
 * Antes o coração era semeado pelo `isFavorite` que vinha DENTRO do payload de
 * catálogo, o que colava estado de usuário no HTML renderizado no servidor e
 * tornava a resposta impossível de compartilhar entre visitantes (ver
 * `public-http.ts`). Agora o catálogo é anônimo e sempre volta
 * `isFavorite: false`; quem sabe o que é favorito é este store.
 *
 * NADA disso acontece no boot. Não existe `FavoritesInit` no layout: favorito é
 * enfeite e não pode disputar rede com carrinho, perfil e primeiro paint. A
 * lista entra por três caminhos, todos sob demanda e deduplicados entre si:
 *
 * - **coração na tela** → `useIsFavorite` pede a hidratação em `requestIdleCallback`;
 * - **clique no coração** → `toggle` garante a lista ANTES de agir (os endpoints
 *   do back são toggle: agir às cegas remove o que o cliente queria adicionar);
 * - **quem já busca a lista inteira** (painel do perfil, widget de favoritos)
 *   chama `loadList`, que semeia os ids de graça — sem segunda requisição.
 *
 * Dois ganhos além de tirar peso do boot:
 * - **Uma fonte da verdade.** Desfavoritar na PDP reflete no card da home e no
 *   painel de favoritos, sem refetch nem prop drilling.
 * - **Menos re-render.** `useIsFavorite` é um seletor booleano: alternar um
 *   coração re-renderiza aquele card, não a grade inteira.
 */

type FavoritesStatus = "idle" | "loading" | "ready" | "error";

/** Referência estável — o seletor não recria um Set a cada render. */
const NO_IDS: ReadonlySet<number> = new Set();

interface FavoritesState {
    ids: ReadonlySet<number>;
    status: FavoritesStatus;
    /** cpfCnpj já carregado — evita refetch a cada coração que monta. */
    loadedFor: string | null;

    /**
     * Busca a lista COMPLETA (com o produto de cada favorito) e semeia os ids.
     * Para quem precisa dos produtos — painel do perfil e widget da home. Volta
     * `null` para convidado. Requisições simultâneas compartilham a mesma
     * chamada em voo.
     */
    loadList: () => Promise<FavoritesList | null>;
    /** Garante os ids do cliente logado — no-op se já carregados ou convidado. */
    ensureLoaded: () => Promise<void>;
    /**
     * Alterna o favorito e persiste. Otimista: pinta na hora e reverte se o
     * backend recusar. Devolve o estado FINAL, ou `null` quando falhou.
     */
    toggle: (productId: number) => Promise<boolean | null>;
    /**
     * O bootstrap ASSUME a carga: `loadedFor` é marcado de forma SÍNCRONA, então
     * a hidratação ociosa que dispara logo em seguida vira no-op em vez de
     * buscar a mesma lista de novo. Quem precisar dos ids antes de a promessa
     * resolver (um coração clicado rápido) espera a MESMA requisição.
     */
    adopt: (cpfCnpj: string, request: Promise<number[]>) => Promise<void>;
    /** Limpa no logout — os favoritos são de quem estava logado. */
    reset: () => void;
}

/** Set novo com o id adicionado ou removido (imutável, para o seletor ver a mudança). */
function withToggled(ids: ReadonlySet<number>, productId: number, favorited: boolean) {
    const next = new Set(ids);
    if (favorited) next.add(productId);
    else next.delete(productId);
    return next;
}

/**
 * Uma única chamada em voo por cliente. É o que impede a home (widget de
 * favoritos + corações da grade) de pedir a mesma lista duas vezes.
 */
let inFlight: { cpfCnpj: string; request: Promise<FavoritesList> } | null = null;

/** Carga assumida pelo bootstrap — quem precisa da lista antes dela resolver, aguarda. */
let adopting: Promise<void> | null = null;

function fetchFavorites(cpfCnpj: string): Promise<FavoritesList> {
    if (inFlight?.cpfCnpj === cpfCnpj) return inFlight.request;

    // O cpfCnpj é só a CHAVE de coalescência aqui — a chamada não o envia: quem
    // resolve a identidade é o BFF, a partir do cookie httpOnly.
    const request = getFavorites();
    const entry = { cpfCnpj, request };
    inFlight = entry;

    const clear = () => {
        if (inFlight === entry) inFlight = null;
    };
    request.then(clear, clear); // o erro em si é tratado por quem chamou

    return request;
}

export const useFavoritesStore = create<FavoritesState>()((set, get) => ({
    ids: NO_IDS,
    status: "idle",
    loadedFor: null,

    loadList: async () => {
        const { user } = useAuthStore.getState();
        if (!user) return null;

        set({ status: "loading" });
        try {
            const list = await fetchFavorites(user.cpfCnpj);
            const ids = list.favorites
                .map((favorite) => favorite.productId)
                .filter((id): id is number => typeof id === "number");

            set({ ids: new Set(ids), status: "ready", loadedFor: user.cpfCnpj });
            return list;
        } catch (error) {
            set({ status: "error", loadedFor: null });
            throw error;
        }
    },

    ensureLoaded: async () => {
        const { user } = useAuthStore.getState();
        if (!user) return;
        if (get().loadedFor === user.cpfCnpj) return;

        try {
            await get().loadList();
        } catch {
            // Silencioso de propósito: favorito é enfeite. Falhar aqui não pode
            // encher a tela de toast — o coração fica vazio e o toggle ainda
            // funciona (o backend é a fonte da verdade).
        }
    },

    toggle: async (productId) => {
        const { user } = useAuthStore.getState();
        if (!user) return null; // convidado: quem chama abre o modal de login

        // Sem lista carregada não dá para decidir add vs remove: os dois
        // endpoints do back são TOGGLE, então chutar errado desfaz o que o
        // cliente pediu.
        if (adopting) await adopting;
        if (get().loadedFor !== user.cpfCnpj) await get().ensureLoaded();

        const favorited = !get().ids.has(productId);
        set({ ids: withToggled(get().ids, productId, favorited) }); // otimista

        try {
            // ATENÇÃO: no back os dois endpoints são TOGGLE (ver favorite.dto).
            // `added`/`removed` dizem o que de fato aconteceu — é por eles que
            // reconciliamos, não pelo que pedimos.
            const result = favorited
                ? await addFavorite(productId, getActiveStoreId() || undefined)
                : await removeFavorite(productId);

            const settled =
                typeof result.added === "boolean"
                    ? result.added
                    : typeof result.removed === "boolean"
                      ? !result.removed
                      : favorited;

            set({ ids: withToggled(get().ids, productId, settled) });
            return settled;
        } catch (error) {
            set({ ids: withToggled(get().ids, productId, !favorited) }); // reverte
            toast.error(getApiErrorMessage(error, "Não foi possível atualizar os favoritos."));
            return null;
        }
    },

    adopt: (cpfCnpj, request) => {
        // Síncrono, ANTES do await: é o que fecha a janela de corrida com a
        // hidratação ociosa (~300ms) enquanto o bootstrap ainda está em voo.
        set({ status: "loading", loadedFor: cpfCnpj });

        const run = request
            .then((ids) => {
                set({ ids: new Set(ids), status: "ready", loadedFor: cpfCnpj });
            })
            .catch(() => {
                // Libera para o caminho de recuperação tentar.
                set({ status: "error", loadedFor: null });
            })
            .finally(() => {
                if (adopting === run) adopting = null;
            });

        adopting = run;
        return run;
    },

    reset: () => set({ ids: NO_IDS, status: "idle", loadedFor: null }),
}));

/** Roda quando o navegador estiver ocioso — nunca junto do boot. */
function whenIdle(run: () => void) {
    if (typeof window === "undefined") return;

    const idle = window.requestIdleCallback;
    if (typeof idle === "function") idle(run, { timeout: 2000 });
    else window.setTimeout(run, 300);
}

/** Já pedimos a hidratação nesta sessão? (um coração basta para pedir). */
let hydrationRequested = false;

/**
 * Sinaliza que existe coração na tela e a lista faz falta. A busca sai em
 * `requestIdleCallback`, ou seja, DEPOIS do que importa (carrinho, perfil,
 * primeiro paint) — e uma vez só por sessão.
 */
export function requestFavoritesHydration() {
    if (hydrationRequested) return;
    hydrationRequested = true;
    whenIdle(() => void useFavoritesStore.getState().ensureLoaded());
}

/**
 * Troca de sessão: limpa os favoritos de quem saiu e, se algum coração já pediu
 * a lista, recarrega para o novo cliente. Substitui o efeito que o antigo
 * `FavoritesInit` fazia no layout — sem componente montado no shell.
 */
if (typeof window !== "undefined") {
    // Semeia com quem já está logado: o módulo pode carregar DEPOIS do login
    // (o primeiro coração é que o importa), e sem isso o logout não limparia nada.
    let currentCpfCnpj: string | null = useAuthStore.getState().user?.cpfCnpj ?? null;

    useAuthStore.subscribe((state) => {
        const cpfCnpj = state.user?.cpfCnpj ?? null;
        if (cpfCnpj === currentCpfCnpj) return;

        currentCpfCnpj = cpfCnpj;
        useFavoritesStore.getState().reset();
        if (cpfCnpj && hydrationRequested) {
            whenIdle(() => void useFavoritesStore.getState().ensureLoaded());
        }
    });
}

/**
 * Este produto é favorito? Seletor booleano — o componente só re-renderiza
 * quando ESTE id entra ou sai da lista.
 *
 * O efeito é o gatilho da hidratação preguiçosa: enquanto não houver coração
 * na tela, o ecommerce não pede favoritos a ninguém.
 */
export const useIsFavorite = (productId: number): boolean => {
    useEffect(() => {
        requestFavoritesHydration();
    }, []);

    return useFavoritesStore((state) => state.ids.has(productId));
};
